import type { GridApi } from '@mui/x-data-grid-pro';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { ItemsApi } from '@/common/api/items';
import type { ItemDependency } from '@/common/api/types/items';
import type { DatasetPage } from '@/common/hooks/useDatasetPages';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { buildIdKeyMatcher } from '../hooks/buildIdKeyMatcher';
import { useCrossCachePagesSync } from '../hooks/useCrossCachePagesSync';
import { useTablePagedQuery } from '../hooks/useTablePagedQuery';
import type { SyncSource } from './VisibleItemsContext';

const EMPTY_DEPENDENCIES: ReadonlyMap<number, ItemDependency[]> = new Map();
const matchDependenciesKey = buildIdKeyMatcher(commonQueryKeys.dependencies.byItemId);
const dependenciesFromSource = (_id: number, sourceData: unknown): ItemDependency[] | undefined =>
  sourceData as ItemDependency[] | undefined;

export type FetchDependenciesByIds = (
  ids: readonly number[],
  signal: AbortSignal,
) => Promise<ReadonlyMap<number, ItemDependency[]>>;

const fetchItemStoreDependencies = async (
  ids: readonly number[],
  signal: AbortSignal,
): Promise<ReadonlyMap<number, ItemDependency[]>> => {
  const items = await ItemsApi.getItemsBulkFetchChunked({
    ids: [...ids],
    fields: ['DEPENDENCIES'],
    signal,
  });
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const dependenciesById = new Map<number, ItemDependency[]>();
  for (const id of ids) {
    dependenciesById.set(id, itemsById.get(id)?.dependencies ?? []);
  }
  return dependenciesById;
};

/**
 * Drop every cached dependency page for this provider/instance, across all
 * dataset signatures. Mirrors `removeVisibleItemsPagesForInstance`: callers
 * outside the provider (e.g. the bulk edit toolbar) evict the instance after a
 * mutation so the mounted signature refetches immediately instead of serving
 * pre-mutation values, which staleTime: Infinity would otherwise pin until a
 * hard refresh.
 */
export const removeVisibleDependenciesPagesForInstance = (
  queryClient: QueryClient,
  instanceKey: string,
): void => {
  queryClient.removeQueries({
    predicate: (query) => {
      const queryKey = query.queryKey;
      return (
        Array.isArray(queryKey) &&
        queryKey[0] === commonQueryKeys.itemsTable.visibleDependencies &&
        queryKey[1] === instanceKey &&
        queryKey[2] === 'page'
      );
    },
  });
};

type VisibleDependenciesContextType = {
  dependenciesById: ReadonlyMap<number, ItemDependency[]>;
};

const VisibleDependenciesContext = React.createContext<VisibleDependenciesContextType | undefined>(
  undefined,
);

export const useVisibleDependencies = (id: number | undefined): ItemDependency[] | undefined => {
  const context = React.useContext(VisibleDependenciesContext);
  if (!context || id === undefined) return undefined;
  return context.dependenciesById.get(id);
};

export const VisibleDependenciesProvider = ({
  pages,
  visiblePageIndices,
  pageIndexById,
  datasetSignature,
  apiRef,
  instanceKey,
  enabled = true,
  fetchDependenciesByIds,
  dependenciesSyncSource,
  children,
}: {
  pages: readonly DatasetPage[];
  visiblePageIndices: readonly number[];
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  apiRef: React.RefObject<GridApi>;
  instanceKey: string;
  enabled?: boolean;
  fetchDependenciesByIds?: FetchDependenciesByIds;
  dependenciesSyncSource?: SyncSource<number, ItemDependency[]>;
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();

  const fetchByIds = React.useCallback(
    async (ids: readonly number[], signal: AbortSignal) => {
      const dependenciesById = fetchDependenciesByIds
        ? await fetchDependenciesByIds(ids, signal)
        : await fetchItemStoreDependencies(ids, signal);
      for (const [id, dependencies] of dependenciesById) {
        queryClient.setQueryData(commonQueryKeys.dependencies.byItemId(id), dependencies);
      }
      return dependenciesById;
    },
    [queryClient, fetchDependenciesByIds],
  );

  const { loadedById } = useTablePagedQuery<ItemDependency[]>({
    providerName: commonQueryKeys.itemsTable.visibleDependencies,
    instanceKey,
    pages,
    visiblePageIndices,
    pageIndexById,
    datasetSignature,
    fetchByIds,
    enabled,
    apiRef,
  });

  useCrossCachePagesSync<ItemDependency[]>({
    providerName: commonQueryKeys.itemsTable.visibleDependencies,
    instanceKey,
    pageIndexById,
    datasetSignature,
    matchKey: dependenciesSyncSource?.matchKey ?? matchDependenciesKey,
    transform: dependenciesSyncSource?.transform ?? dependenciesFromSource,
  });

  const value = React.useMemo<VisibleDependenciesContextType>(
    () => ({ dependenciesById: loadedById.size > 0 ? loadedById : EMPTY_DEPENDENCIES }),
    [loadedById],
  );

  return (
    <VisibleDependenciesContext.Provider value={value}>
      {children}
    </VisibleDependenciesContext.Provider>
  );
};
